import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, UserRole } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Target,
    Plus,
    Truck,
    Users,
    ChevronDown,
    ChevronUp,
    Power,
    Trash2,
    Edit,
    GitBranch,
    UserCheck,
    CheckCircle2,
    Clock,
    X,
    UserPlus,
    UserMinus
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MissionsPage = () => {
    const { token, user } = useAuth();
    const [missions, setMissions] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [showSubCreate, setShowSubCreate] = useState(null);
    const [expandedMission, setExpandedMission] = useState(null);
    const [filter, setFilter] = useState('all');

    const [form, setForm] = useState({
        title: '', description: '', assigned_vehicle_ids: [], assigned_personnel: [],
        chef_secteur_id: '', chef_secteur_name: ''
    });
    const [subForm, setSubForm] = useState({ title: '', description: '', responsable_id: '', responsable_name: '' });
    const [editingMission, setEditingMission] = useState(null);
    const [editingSubMission, setEditingSubMission] = useState(null);

    const isChef = user?.role === UserRole.CHEF_DISPOSITIF;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = useCallback(async () => {
        try {
            const [missionsRes, vehiclesRes, usersRes] = await Promise.all([
                axios.get(`${API}/missions`, { headers }),
                axios.get(`${API}/vehicles`, { headers }),
                axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] }))
            ]);
            setMissions(missionsRes.data);
            setVehicles(vehiclesRes.data);
            setAllUsers(usersRes.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCreate = async () => {
        if (!form.title.trim()) { toast.error('Titre requis'); return; }
        try {
            await axios.post(`${API}/missions`, {
                title: form.title,
                description: form.description,
                assigned_vehicle_ids: form.assigned_vehicle_ids,
                assigned_personnel: form.assigned_personnel,
                chef_secteur_id: form.chef_secteur_id || null,
                chef_secteur_name: form.chef_secteur_name
            }, { headers });
            toast.success('Mission creee');
            setShowCreate(false);
            setForm({ title: '', description: '', assigned_vehicle_ids: [], assigned_personnel: [], chef_secteur_id: '', chef_secteur_name: '' });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleToggle = async (id) => {
        try {
            const res = await axios.put(`${API}/missions/${id}/toggle`, {}, { headers });
            toast.success(res.data.message);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cette mission ?')) return;
        try {
            await axios.delete(`${API}/missions/${id}`, { headers });
            toast.success('Mission supprimee');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleCreateSub = async (missionId) => {
        if (!subForm.title.trim()) { toast.error('Titre requis'); return; }
        try {
            await axios.post(`${API}/missions/${missionId}/sub-missions`, subForm, { headers });
            toast.success('Sous-mission creee');
            setShowSubCreate(null);
            setSubForm({ title: '', description: '', responsable_id: '', responsable_name: '' });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleEditMission = (mission) => {
        setEditingMission(mission);
        setForm({
            title: mission.title,
            description: mission.description || '',
            assigned_vehicle_ids: mission.assigned_vehicles?.map(v => v.id) || [],
            assigned_personnel: mission.assigned_personnel || [],
            chef_secteur_id: mission.chef_secteur_id || '',
            chef_secteur_name: mission.chef_secteur_name || ''
        });
        setShowCreate(true);
    };

    const handleUpdateMission = async () => {
        if (!form.title.trim()) { toast.error('Titre requis'); return; }
        try {
            await axios.put(`${API}/missions/${editingMission.id}`, {
                title: form.title,
                description: form.description,
                assigned_vehicle_ids: form.assigned_vehicle_ids,
                assigned_personnel: form.assigned_personnel,
                chef_secteur_id: form.chef_secteur_id || null,
                chef_secteur_name: form.chef_secteur_name
            }, { headers });
            toast.success('Mission mise a jour');
            setShowCreate(false);
            setEditingMission(null);
            setForm({ title: '', description: '', assigned_vehicle_ids: [], assigned_personnel: [], chef_secteur_id: '', chef_secteur_name: '' });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleToggleSub = async (missionId, subId) => {
        try {
            await axios.put(`${API}/missions/${missionId}/sub-missions/${subId}/toggle`, {}, { headers });
            toast.success('Sous-mission mise a jour');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleEditSub = (missionId, sub) => {
        setEditingSubMission({ missionId, sub });
        setSubForm({ title: sub.title, description: sub.description || '', responsable_id: sub.responsable_id || '', responsable_name: sub.responsable_name || '' });
        setShowSubCreate(missionId);
    };

    const handleUpdateSub = async () => {
        if (!subForm.title.trim()) { toast.error('Titre requis'); return; }
        try {
            await axios.put(`${API}/missions/${editingSubMission.missionId}/sub-missions/${editingSubMission.sub.id}`, subForm, { headers });
            toast.success('Sous-mission mise a jour');
            setShowSubCreate(null);
            setEditingSubMission(null);
            setSubForm({ title: '', description: '', responsable_id: '', responsable_name: '' });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const SUB_MISSION_TEMPLATES = [
        { title: 'Etat des degats', description: 'Evaluation et inventaire des degats sur le secteur' },
        { title: 'Desencombrement', description: 'Nettoyage et degagement des voies et zones impactees' },
        { title: 'Securisation des batiments', description: 'Verification et mise en securite des structures endommagees' },
        { title: 'Retablissement des reseaux', description: 'Verification eau, electricite, gaz, telecommunications' },
        { title: 'Relogement des sinistres', description: 'Organisation de l\'hebergement provisoire des personnes deplacees' },
        { title: 'Soutien psychologique', description: 'Mise en place d\'un dispositif CUMP pour les victimes' },
        { title: 'Communication population', description: 'Information aux habitants sur la situation et les consignes' },
        { title: 'Ravitaillement', description: 'Distribution de nourriture, eau et produits de premiere necessite' },
    ];

    const applyTemplate = (tpl) => {
        setSubForm(f => ({ ...f, title: tpl.title, description: tpl.description }));
    };

    const toggleVehicle = (vid) => {
        setForm(f => ({
            ...f,
            assigned_vehicle_ids: f.assigned_vehicle_ids.includes(vid)
                ? f.assigned_vehicle_ids.filter(v => v !== vid)
                : [...f.assigned_vehicle_ids, vid]
        }));
    };

    const togglePersonnel = (u) => {
        setForm(f => {
            const exists = f.assigned_personnel.find(p => p.id === u.id);
            return {
                ...f,
                assigned_personnel: exists
                    ? f.assigned_personnel.filter(p => p.id !== u.id)
                    : [...f.assigned_personnel, { id: u.id, name: u.name, role: u.role }]
            };
        });
    };

    const availableVehicles = vehicles.filter(v => v.status === 'available' || v.status === 'disponible');

    const [personnelDialogMission, setPersonnelDialogMission] = useState(null);
    const [selectedPersonnelIds, setSelectedPersonnelIds] = useState([]);

    const handleAssignPersonnelDirect = async (missionId) => {
        if (selectedPersonnelIds.length === 0) return;
        try {
            for (const uid of selectedPersonnelIds) {
                const u = allUsers.find(x => x.id === uid);
                if (!u) continue;
                await axios.put(`${API}/missions/${missionId}/assign-personnel`, {
                    id: u.id, name: u.name, role: u.role
                }, { headers });
            }
            toast.success(`${selectedPersonnelIds.length} personne(s) assignee(s)`);
            setPersonnelDialogMission(null);
            setSelectedPersonnelIds([]);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleRemovePersonnelDirect = async (missionId, personId) => {
        try {
            await axios.put(`${API}/missions/${missionId}/remove-personnel?person_id=${encodeURIComponent(personId)}`, {}, { headers });
            toast.success('Personnel retire');
            fetchData();
        } catch (err) {
            toast.error('Erreur');
        }
    };

    const filtered = missions.filter(m => {
        if (filter === 'active') return m.status === 'active';
        if (filter === 'closed') return m.status === 'closed';
        if (filter === 'sub') return (m.sub_missions?.length || 0) > 0;
        return true;
    });

    if (loading) {
        return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;
    }

    return (
        <div className="space-y-6" data-testid="missions-page">
            <div className="page-header">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-white">Retour à la normalité</h1>
                        <p className="page-subtitle">Gestion des missions de retour à la normalité</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {isChef && (
                            <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="create-mission-btn">
                                <Plus className="w-4 h-4 mr-2" /> Nouvelle mission
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats - Clickable filters */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <Card 
                    className={`stat-card border-l-blue-500 cursor-pointer transition-all hover:ring-1 hover:ring-blue-500/50 ${filter === 'active' ? 'ring-2 ring-blue-500 bg-blue-500/5' : ''}`}
                    onClick={() => setFilter(filter === 'active' ? 'all' : 'active')}
                    data-testid="filter-active"
                >
                    <CardContent className="p-0">
                        <p className="stat-value text-blue-400">{missions.filter(m => m.status === 'active').length}</p>
                        <p className="stat-label">Missions actives</p>
                        {filter === 'active' && <p className="text-[10px] text-blue-400 mt-1">Filtre actif</p>}
                    </CardContent>
                </Card>
                <Card 
                    className={`stat-card border-l-green-500 cursor-pointer transition-all hover:ring-1 hover:ring-green-500/50 ${filter === 'closed' ? 'ring-2 ring-green-500 bg-green-500/5' : ''}`}
                    onClick={() => setFilter(filter === 'closed' ? 'all' : 'closed')}
                    data-testid="filter-closed"
                >
                    <CardContent className="p-0">
                        <p className="stat-value text-green-400">{missions.filter(m => m.status === 'closed').length}</p>
                        <p className="stat-label">Missions cloturees</p>
                        {filter === 'closed' && <p className="text-[10px] text-green-400 mt-1">Filtre actif</p>}
                    </CardContent>
                </Card>
                <Card 
                    className={`stat-card border-l-purple-500 cursor-pointer transition-all hover:ring-1 hover:ring-purple-500/50 ${filter === 'sub' ? 'ring-2 ring-purple-500 bg-purple-500/5' : ''}`}
                    onClick={() => setFilter(filter === 'sub' ? 'all' : 'sub')}
                    data-testid="filter-sub"
                >
                    <CardContent className="p-0">
                        <p className="stat-value text-purple-400">{missions.reduce((acc, m) => acc + (m.sub_missions?.length || 0), 0)}</p>
                        <p className="stat-label">Sous-missions</p>
                        {filter === 'sub' && <p className="text-[10px] text-purple-400 mt-1">Filtre actif</p>}
                    </CardContent>
                </Card>
            </div>

            {/* Missions List */}
            {filtered.length === 0 ? (
                <Card className="content-card">
                    <CardContent className="p-12 text-center">
                        <Target className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                        <p className="text-zinc-400">Aucune mission</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {filtered.map(mission => (
                        <Card key={mission.id} className="content-card overflow-hidden" data-testid={`mission-card-${mission.id}`}>
                            <div className="p-5">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-1">
                                            <Target className={`w-5 h-5 flex-shrink-0 ${mission.status === 'active' ? 'text-blue-400' : 'text-zinc-500'}`} />
                                            <h3 className="text-lg font-semibold text-white truncate">{mission.title}</h3>
                                            <Badge className={mission.status === 'active' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-zinc-700/50 text-zinc-400 border-zinc-600/30'}>
                                                {mission.status === 'active' ? 'Active' : 'Cloturee'}
                                            </Badge>
                                        </div>
                                        {mission.description && <p className="text-sm text-zinc-400 ml-8">{mission.description}</p>}
                                        <div className="flex items-center gap-4 mt-2 ml-8 text-xs text-zinc-500">
                                            {mission.chef_secteur_name && (
                                                <span className="flex items-center gap-1"><UserCheck className="w-3 h-3" /> Chef: {mission.chef_secteur_name}</span>
                                            )}
                                            <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> {mission.assigned_vehicles?.length || 0} vehicule(s)</span>
                                            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {mission.assigned_personnel?.length || 0} personnel(s)</span>
                                            <span className="flex items-center gap-1"><GitBranch className="w-3 h-3" /> {mission.sub_missions?.length || 0} sous-mission(s)</span>
                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(mission.created_at).toLocaleDateString('fr-FR')}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <Button variant="ghost" size="sm" onClick={() => setExpandedMission(expandedMission === mission.id ? null : mission.id)} data-testid={`expand-mission-${mission.id}`}>
                                            {expandedMission === mission.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        </Button>
                                        {isChef && (
                                            <>
                                                <Button variant="ghost" size="sm" onClick={() => handleEditMission(mission)} className="text-blue-400 hover:text-blue-300" data-testid={`edit-mission-${mission.id}`}>
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleToggle(mission.id)} data-testid={`toggle-mission-${mission.id}`}
                                                    className={mission.status === 'active' ? 'text-amber-400 hover:text-amber-300' : 'text-green-400 hover:text-green-300'}>
                                                    <Power className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDelete(mission.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-mission-${mission.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Expanded Details */}
                                {expandedMission === mission.id && (
                                    <div className="mt-4 ml-8 space-y-4 border-t border-zinc-800 pt-4">
                                        {/* Vehicles */}
                                        {mission.assigned_vehicles?.length > 0 && (
                                            <div>
                                                <p className="text-xs font-medium text-zinc-400 mb-2 flex items-center gap-1"><Truck className="w-3 h-3" /> Vehicules assignes</p>
                                                <div className="flex flex-wrap gap-2">
                                                    {mission.assigned_vehicles.map(v => (
                                                        <Badge key={v.id} className="bg-amber-500/20 text-amber-400 border-amber-500/30">{v.name}</Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Personnel */}
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-xs font-medium text-zinc-400 flex items-center gap-1"><Users className="w-3 h-3" /> Personnel assigne ({mission.assigned_personnel?.length || 0})</p>
                                                {isChef && mission.status === 'active' && (
                                                    <Button variant="ghost" size="sm" onClick={() => { setPersonnelDialogMission(mission); setSelectedPersonnelIds([]); }} className="text-cyan-400 h-7 text-xs" data-testid={`add-personnel-mission-${mission.id}`}>
                                                        <UserPlus className="w-3 h-3 mr-1" /> Ajouter
                                                    </Button>
                                                )}
                                            </div>
                                            {mission.assigned_personnel?.length > 0 ? (
                                                <div className="flex flex-wrap gap-2">
                                                    {mission.assigned_personnel.map((p) => (
                                                        <Badge key={p.id} className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 flex items-center gap-1">
                                                            {p.name} ({p.role})
                                                            {isChef && (
                                                                <button onClick={() => handleRemovePersonnelDirect(mission.id, p.id)} className="ml-1 text-red-400 hover:text-red-300" data-testid={`remove-personnel-${mission.id}-${p.id}`}>
                                                                    <UserMinus className="w-3 h-3" />
                                                                </button>
                                                            )}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-zinc-600">Aucun personnel assigne</p>
                                            )}
                                        </div>

                                        {/* Sub-missions */}
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-xs font-medium text-zinc-400 flex items-center gap-1"><GitBranch className="w-3 h-3" /> Sous-missions</p>
                                                {isChef && mission.status === 'active' && (
                                                    <Button variant="ghost" size="sm" onClick={() => setShowSubCreate(mission.id)} className="text-blue-400 h-7 text-xs" data-testid={`add-sub-mission-${mission.id}`}>
                                                        <Plus className="w-3 h-3 mr-1" /> Ajouter
                                                    </Button>
                                                )}
                                            </div>
                                            {mission.sub_missions?.length > 0 ? (
                                                <div className="space-y-2">
                                                    {mission.sub_missions.map(sub => (
                                                        <div key={sub.id} className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50" data-testid={`sub-mission-${sub.id}`}>
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    <CheckCircle2 className={`w-4 h-4 ${sub.status === 'active' ? 'text-blue-400' : 'text-green-400'}`} />
                                                                    <span className={`font-medium text-sm ${sub.status === 'closed' ? 'text-zinc-500 line-through' : 'text-white'}`}>{sub.title}</span>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    {isChef && (
                                                                        <>
                                                                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-400 hover:text-blue-300" onClick={() => handleEditSub(mission.id, sub)} data-testid={`edit-sub-${sub.id}`}>
                                                                                <Edit className="w-3 h-3" />
                                                                            </Button>
                                                                            <Button variant="ghost" size="sm" className={`h-6 w-6 p-0 ${sub.status === 'active' ? 'text-amber-400 hover:text-amber-300' : 'text-green-400 hover:text-green-300'}`} onClick={() => handleToggleSub(mission.id, sub.id)} data-testid={`toggle-sub-${sub.id}`}>
                                                                                <Power className="w-3 h-3" />
                                                                            </Button>
                                                                        </>
                                                                    )}
                                                                    <Badge className={sub.status === 'active' ? 'bg-blue-500/20 text-blue-400 text-xs' : 'bg-green-500/20 text-green-400 text-xs'}>
                                                                        {sub.status === 'active' ? 'Active' : 'Terminee'}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                            {sub.description && <p className="text-xs text-zinc-500 mt-1 ml-6">{sub.description}</p>}
                                                            {sub.responsable_name && <p className="text-xs text-zinc-400 mt-1 ml-6">Responsable: {sub.responsable_name}</p>}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-zinc-600 italic">Aucune sous-mission</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Create/Edit Mission Dialog */}
            <Dialog open={showCreate} onOpenChange={(open) => { if (!open) { setShowCreate(false); setEditingMission(null); setForm({ title: '', description: '', assigned_vehicle_ids: [], assigned_personnel: [], chef_secteur_id: '', chef_secteur_name: '' }); } }}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white">{editingMission ? 'Modifier la mission' : 'Nouvelle mission'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm text-zinc-400">Titre *</label>
                            <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}
                                className="bg-zinc-800 border-zinc-700 text-white" placeholder="Ex: Nettoyage secteur Nord" data-testid="mission-title-input" />
                        </div>
                        <div>
                            <label className="text-sm text-zinc-400">Description</label>
                            <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                                className="bg-zinc-800 border-zinc-700 text-white" placeholder="Details de la mission" data-testid="mission-desc-input" />
                        </div>
                        <div>
                            <label className="text-sm text-zinc-400">Chef de secteur</label>
                            <Select value={form.chef_secteur_id} onValueChange={v => {
                                const u = allUsers.find(usr => usr.id === v);
                                setForm(f => ({...f, chef_secteur_id: v, chef_secteur_name: u?.name || ''}));
                            }}>
                                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="mission-chef-select">
                                    <SelectValue placeholder="Selectionner" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                    {allUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Vehicle Selection */}
                        <div>
                            <label className="text-sm text-zinc-400 flex items-center gap-1"><Truck className="w-3 h-3" /> Vehicules disponibles</label>
                            <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                {availableVehicles.length > 0 ? availableVehicles.map(v => (
                                    <Badge key={v.id} onClick={() => toggleVehicle(v.id)}
                                        className={`cursor-pointer transition-colors ${form.assigned_vehicle_ids.includes(v.id) ? 'bg-amber-500/30 text-amber-300 border-amber-500' : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'}`}
                                        data-testid={`vehicle-badge-${v.id}`}>
                                        {v.name}
                                    </Badge>
                                )) : <p className="text-xs text-zinc-600 italic">Aucun vehicule disponible</p>}
                            </div>
                        </div>

                        {/* Personnel Selection */}
                        <div>
                            <label className="text-sm text-zinc-400 flex items-center gap-1"><Users className="w-3 h-3" /> Personnel</label>
                            <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                {allUsers.map(u => (
                                    <Badge key={u.id} onClick={() => togglePersonnel(u)}
                                        className={`cursor-pointer transition-colors ${form.assigned_personnel.find(p => p.id === u.id) ? 'bg-cyan-500/30 text-cyan-300 border-cyan-500' : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'}`}
                                        data-testid={`personnel-badge-${u.id}`}>
                                        {u.name}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setShowCreate(false); setEditingMission(null); }}>Annuler</Button>
                        <Button onClick={editingMission ? handleUpdateMission : handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-create-mission">
                            {editingMission ? 'Enregistrer' : 'Creer la mission'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create/Edit Sub-Mission Dialog */}
            <Dialog open={!!showSubCreate} onOpenChange={() => { setShowSubCreate(null); setEditingSubMission(null); setSubForm({ title: '', description: '', responsable_id: '', responsable_name: '' }); }}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white">{editingSubMission ? 'Modifier la sous-mission' : 'Nouvelle sous-mission'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Templates */}
                        {!editingSubMission && (
                            <div>
                                <label className="text-sm text-zinc-400 mb-2 block">Modeles de sous-missions</label>
                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                    {SUB_MISSION_TEMPLATES.map((tpl, i) => (
                                        <Badge key={tpl.title} onClick={() => applyTemplate(tpl)}
                                            className="cursor-pointer bg-zinc-800 text-zinc-300 border-zinc-700 hover:border-blue-500/50 hover:text-blue-400 transition-colors text-xs"
                                            data-testid={`template-${i}`}>
                                            {tpl.title}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="text-sm text-zinc-400">Titre *</label>
                            <Input value={subForm.title} onChange={e => setSubForm(f => ({...f, title: e.target.value}))}
                                className="bg-zinc-800 border-zinc-700 text-white" data-testid="sub-mission-title-input" />
                        </div>
                        <div>
                            <label className="text-sm text-zinc-400">Description</label>
                            <Input value={subForm.description} onChange={e => setSubForm(f => ({...f, description: e.target.value}))}
                                className="bg-zinc-800 border-zinc-700 text-white" data-testid="sub-mission-desc-input" />
                        </div>
                        <div>
                            <label className="text-sm text-zinc-400">Responsable</label>
                            <Select value={subForm.responsable_id} onValueChange={v => {
                                const u = allUsers.find(usr => usr.id === v);
                                setSubForm(f => ({...f, responsable_id: v, responsable_name: u?.name || ''}));
                            }}>
                                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="sub-mission-responsable-select">
                                    <SelectValue placeholder="Selectionner un responsable" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                    {allUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setShowSubCreate(null); setEditingSubMission(null); }}>Annuler</Button>
                        <Button onClick={editingSubMission ? handleUpdateSub : () => handleCreateSub(showSubCreate)} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-create-sub-mission">
                            {editingSubMission ? 'Enregistrer' : 'Creer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Personnel Assignment Dialog */}
            <Dialog open={!!personnelDialogMission} onOpenChange={(open) => { if (!open) { setPersonnelDialogMission(null); setSelectedPersonnelIds([]); } }}>
                <DialogContent className="max-w-md bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-cyan-500" />
                            Ajouter du personnel - {personnelDialogMission?.title}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="max-h-[300px] overflow-y-auto space-y-1 border border-zinc-700 rounded-lg p-2" data-testid="mission-personnel-list">
                            {allUsers
                                .filter(u => u.role !== 'pending' && u.role !== 'commune')
                                .filter(u => !(personnelDialogMission?.assigned_personnel || []).some(p => p.id === u.id))
                                .map(u => {
                                    const checked = selectedPersonnelIds.includes(u.id);
                                    return (
                                        <label key={u.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${checked ? 'bg-cyan-500/10 border border-cyan-500/30' : 'hover:bg-zinc-800 border border-transparent'}`}>
                                            <input type="checkbox" checked={checked} onChange={(e) => {
                                                if (e.target.checked) setSelectedPersonnelIds([...selectedPersonnelIds, u.id]);
                                                else setSelectedPersonnelIds(selectedPersonnelIds.filter(x => x !== u.id));
                                            }} className="accent-cyan-500" data-testid={`mission-personnel-check-${u.id}`} />
                                            <div className="flex-1">
                                                <p className="text-sm text-white font-medium">{u.name}</p>
                                                <p className="text-xs text-zinc-500">{u.role}</p>
                                            </div>
                                        </label>
                                    );
                                })}
                        </div>
                        {selectedPersonnelIds.length > 0 && <p className="text-xs text-cyan-400">{selectedPersonnelIds.length} personne(s) selectionnee(s)</p>}
                        <DialogFooter>
                            <Button variant="ghost" onClick={() => { setPersonnelDialogMission(null); setSelectedPersonnelIds([]); }}>Annuler</Button>
                            <Button onClick={() => handleAssignPersonnelDirect(personnelDialogMission?.id)} className="bg-cyan-600 hover:bg-cyan-700 text-white" disabled={selectedPersonnelIds.length === 0} data-testid="confirm-assign-personnel">
                                <UserPlus className="w-4 h-4 mr-2" /> Assigner ({selectedPersonnelIds.length})
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MissionsPage;
